import { useEffect, useState, useMemo } from "react";
import CustomCard from "../../components/CustomCard/CustomCard";
import { useSelector, useDispatch } from "react-redux";
import useSearchableTable from "../../hooks/useSearchableTable";
import useModuleContext from "../../hooks/useModuleContext";
import TableHeader from "../../components/CustomTable/TableHeader";
import { getBusEnt, getCreditLimitRequests } from "../../redux/slices/creditLimitSlice";
import { getStatusList } from "../../redux/slices/EnqMngtSlice";
import CreditLimitTable from "./Components/CreditLimitTable";
import { downloadPageHandle, generateStatusTableTitle } from "../../helpers/helpers";
import { CREDIT_LIMIT_MSGS } from "../../constants/constants";
import {
  CREDIT_STATUS_DISPLAY_NAMES,
  ENT_STATUS,
  TITLE_TXT,
  BTN_TXT,
  FORM_MODAL_WIDTHS,
} from "../../constants/constants";
import CustomModal from "../../components/CustomModal/CustomModal";
import "./CreditLimit.scss";
import NewCreditLimitForm from "./Components/NewCreditLimitForm";
import CreditLimitRequestsTable from "./Components/CreditLimitRequestsTable";
import { PERM_CODE, CREDIT_STATUS_PRIVILEGE_MAP } from "../../constants/PermissionCodes";
import { Spin, Tabs, message } from "antd";
import { count } from "../../redux/slices/dealsSlice";

const buildStatusTableTitle = ({ defaultTitle = "" } = {}) => defaultTitle || "";

const CreditLimit = () => {
  const dispatch = useDispatch();
  const { creditLimitData, requestsData, loading, error } = useSelector((state) => state?.creditLimit);
  const { statusList } = useSelector((state) => state?.enqMngt);
  const { user, allCountData } = useSelector((state) => state.auth);
  const { CountData } = useSelector((state) => state?.deals);
  const { loading: docLoading, error: docError } = useSelector((state) => state.documents);

  const [modal, setModal] = useState(false);
  const [columnControl, setColumnControl] = useState(null);

  // Derive user privilege codes
  const userPrivileges = useMemo(() => {
    const raw = user?.privilege || [];
    return raw
      .map((p) => (typeof p === "string" ? p : p?.privilegeCode))
      .filter(Boolean);
  }, [user]);

  // Compute allowed statuses for this user
  const allowedStatusIds = useMemo(
    () => userPrivileges.flatMap((priv) => CREDIT_STATUS_PRIVILEGE_MAP[priv] || []),
    [userPrivileges]
  );

  const DEFAULT_CREDIT_STATUSES = useMemo(() => [
    ENT_STATUS.PENDING.ID,
    ENT_STATUS.ACTIVE.ID,
  ], []);

  const defaultStatus = useMemo(() => {
    const preferred = DEFAULT_CREDIT_STATUSES.filter((id) =>
      allowedStatusIds.includes(id)
    );
    return preferred.length > 0 ? preferred : null;
  }, [allowedStatusIds, DEFAULT_CREDIT_STATUSES]);

  const {
    selectedStatus,
    handleStatusChange,
    searchText,
    handleSearchChange,
    activeTab,
    handleTabChange,
    pagination,
    applyDefaultStatus,
  } = useModuleContext({
    moduleKey: "creditLimit",
    isMultiStatus: true,
    allowedStatusIds,
    defaultStatus,
    defaultTab: "requests",
  });

  // Fetch statuses + pick default

  useEffect(() => {
    const fetchStatus = async () => {
      const res = await dispatch(getStatusList("busEnt"));
      if (res.payload?.statusCode === 200) {
        const preferredStatuses = DEFAULT_CREDIT_STATUSES.filter((id) =>
          allowedStatusIds.includes(id),
        );
        const fallback =
          preferredStatuses.length > 0
            ? preferredStatuses
            : allowedStatusIds[0]
            ? [allowedStatusIds[0]]
            : [];
        applyDefaultStatus(fallback);
      }
    };
    fetchStatus();
  }, [dispatch, allowedStatusIds, applyDefaultStatus, DEFAULT_CREDIT_STATUSES]);

  // Fetch credit-limit data for selected status
  useEffect(() => {
    if (selectedStatus) {
      dispatch(getBusEnt(selectedStatus)).finally(() => {
        dispatch(count("CREDITLIMIT"));
      });
    }
  }, [dispatch, selectedStatus]);

  // Fetch deals count and credit limit requests once
  useEffect(() => {
    dispatch(count("CREDITLIMIT"));
    dispatch(getCreditLimitRequests());
  }, [dispatch]);

  // Table search/filter hook
  const { filteredData } =
    useSearchableTable(creditLimitData, searchText, handleSearchChange);

  // Limit dropdown statuses by privileges
  const filteredStatusList = statusList?.filter((status) =>
    allowedStatusIds.includes(status.statusId)
  );

  // Merge counts **only for IsDeal statuses**
  const isDealStatusIds = Object.values(ENT_STATUS).map((s) => s.ID);
  const listCount = Array.isArray(creditLimitData) ? creditLimitData.length : null;
  const statusListWithCounts = filteredStatusList.map((item) => {
    const isDeal = isDealStatusIds.includes(item.statusId);
    let count = isDeal ? CountData?.find((c) => c.statusId === item.statusId)?.count || 0 : undefined;

    // Keep badge/list in sync once the table data has loaded (only for single selected status)
    if (
      (!Array.isArray(selectedStatus) || selectedStatus.length === 1) &&
      (Array.isArray(selectedStatus) ? selectedStatus.includes(item.statusId) : item.statusId === selectedStatus) &&
      listCount !== null &&
      !loading
    ) {
      count = listCount;
    }

    return {
      ...item,
      count: isDeal ? count : undefined,
    };
  });

  const tableTitle = useMemo(
    () =>
      (generateStatusTableTitle || buildStatusTableTitle)({
        defaultTitle: "Credit Limit Requests",
      }),
    [],
  );

  // ============================
  // ✅ DOWNLOAD HANDLERS
  // ============================

  const handleDownload = (type) => downloadPageHandle({ dispatch, type, screenName: "creditLimit", statusId: selectedStatus });

  useEffect(() => {
    if (error) {
      message.error(
        typeof error === "string"
          ? error
          : "Unable to load credit limit records. Please refresh the page.",
      );
    }
  }, [error]);

  useEffect(() => {
    if (docError) {
      message.error(CREDIT_LIMIT_MSGS.DOWNLOAD_FAILED);
    }
  }, [docError]);

  const requestsCount = requestsData?.length || 0;
  const creditLimitTotalCount = useMemo(() => {
    if (allCountData?.creditLimit !== undefined && allCountData?.creditLimit !== null) {
      return Number(allCountData.creditLimit) || 0;
    }
    if (Array.isArray(CountData) && CountData.length > 0) {
      const dealCounts = CountData.filter((c) => isDealStatusIds.includes(c.statusId));
      if (dealCounts.length > 0) {
        return dealCounts.reduce((acc, curr) => acc + (Number(curr.count) || 0), 0);
      }
    }
    return Array.isArray(creditLimitData) ? creditLimitData.length : 0;
  }, [allCountData, CountData, isDealStatusIds, creditLimitData]);

  const tabItems = [
    {
      key: "requests",
      label: `Request (${requestsCount})`,
      children: (
        <CreditLimitRequestsTable selectedExistingStatus={selectedStatus} />
      ),
    },
    {
      key: "creditLimit",
      label: `Credit Limit (${creditLimitTotalCount})`,
      forceRender: true,
      children: (
        <Spin spinning={loading}>
          <TableHeader
            title={tableTitle}
            searchText={searchText}
            setSearchText={handleSearchChange}
            isDropdown={true}
            dropdownData={statusListWithCounts}
            selectedStatus={selectedStatus}
            handleStatusChange={handleStatusChange}
            statusDisplayNames={CREDIT_STATUS_DISPLAY_NAMES}
            btnName={userPrivileges.includes(PERM_CODE.CAACC) ? BTN_TXT.ADD_NEW_CREDIT_LIMIT : undefined}
            handleBtnOnClick={() => setModal(true)}
            isDownloadRequired={true}
            onDownloadPdf={() => handleDownload("pdf")}     // ✅
            onDownloadExcel={() => handleDownload("excel")} // ✅
            downloadLoading={docLoading}
            columnControl={columnControl}
          />

          <CreditLimitTable
            tableData={filteredData}
            selectedStatus={selectedStatus}
            pagination={pagination}
            onColumnControlChange={setColumnControl}
          />

          {modal && (
            <CustomModal
              title={TITLE_TXT.ADD_NEW_CREDIT_LIMIT}
              open
              centered
              width={FORM_MODAL_WIDTHS}
              content={
                <NewCreditLimitForm
                  onClose={() => setModal(false)}
                  selectedStatus={selectedStatus}
                />
              }
            />
          )}
        </Spin>
      ),
    },
  ];

  return (
    <CustomCard>
      <Tabs activeKey={activeTab} onChange={handleTabChange} items={tabItems} className="credit-limit-tabs" />
    </CustomCard>
  );
};

export default CreditLimit;
